import React, { useEffect, useState } from "react";
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    Typography,
    Button,
    TextField,
    IconButton,
    CircularProgress,
    Switch,
    FormControlLabel,
} from "@mui/material";
import { Save, Add, Delete } from "@mui/icons-material";
import { useSnackbar } from "notistack";

import systemSettingsService from "../../services/systemSettingsService";
import PageHeader from "../../components/common/PageHeader";

const blankTier = {
    name: "",
    price: "",
    maxCbf: "",
    consolidation: "",
    insuranceLabel: "",
    highlight: false,
    highlightLabel: "",
};

const AdminShopperRates = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tiers, setTiers] = useState([]);
    const [deliveryFees, setDeliveryFees] = useState([]);
    const [perks, setPerks] = useState([]);

    useEffect(() => {
        systemSettingsService
            .get()
            .then(({ data }) => {
                const s = data.data.settings;
                setTiers(s.shopperRateTiers || []);
                setDeliveryFees(
                    s.shopperRateDeliveryFees?.length
                        ? s.shopperRateDeliveryFees
                        : [{ route: s.shopperRateDeliveryRoute || "", fee: s.shopperRateDeliveryFee ?? "" }],
                );
                setPerks(s.shopperRatePerks || []);
            })
            .catch(() => enqueueSnackbar("Failed to load rates", { variant: "error" }))
            .finally(() => setLoading(false));
    }, []);

    const updateTier = (idx, field) => (e) => {
        const value = field === "highlight" ? e.target.checked : e.target.value;
        setTiers((prev) => prev.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
    };

    const addTier = () => setTiers((prev) => [...prev, { ...blankTier }]);
    const removeTier = (idx) => setTiers((prev) => prev.filter((_, i) => i !== idx));

    const updateDeliveryFee = (idx, field) => (e) =>
        setDeliveryFees((prev) => prev.map((d, i) => (i === idx ? { ...d, [field]: e.target.value } : d)));
    const addDeliveryFee = () => setDeliveryFees((prev) => [...prev, { route: "", fee: "" }]);
    const removeDeliveryFee = (idx) => setDeliveryFees((prev) => prev.filter((_, i) => i !== idx));

    const updatePerk = (idx) => (e) =>
        setPerks((prev) => prev.map((p, i) => (i === idx ? e.target.value : p)));
    const addPerk = () => setPerks((prev) => [...prev, ""]);
    const removePerk = (idx) => setPerks((prev) => prev.filter((_, i) => i !== idx));

    const handleSave = async () => {
        setSaving(true);
        try {
            await systemSettingsService.update({
                shopperRateTiers: tiers.map((t) => ({
                    ...t,
                    price: parseFloat(t.price) || 0,
                    maxCbf: parseFloat(t.maxCbf) || 0,
                })),
                shopperRateDeliveryFees: deliveryFees
                    .filter((d) => d.route.trim())
                    .map((d) => ({ route: d.route.trim(), fee: parseFloat(d.fee) || 0 })),
                shopperRatePerks: perks.filter((p) => p.trim()),
            });
            enqueueSnackbar("Rates updated", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save rates", {
                variant: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Online Shopper Flat Rates"
                subtitle="Edit the pricing shown on the public rates page"
            />

            <Card>
                <CardHeader
                    title={<Typography fontWeight={700} fontSize={14}>Rate Tiers</Typography>}
                    action={
                        <Button size="small" startIcon={<Add />} onClick={addTier}>
                            Add Tier
                        </Button>
                    }
                />
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {tiers.map((tier, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "1fr", sm: "1fr 120px 120px" },
                                    gap: 1.5,
                                }}
                            >
                                <TextField
                                    size="small"
                                    label="Tier Name"
                                    value={tier.name}
                                    onChange={updateTier(idx, "name")}
                                />
                                <TextField
                                    size="small"
                                    label="Price ($)"
                                    type="number"
                                    value={tier.price}
                                    onChange={updateTier(idx, "price")}
                                    inputProps={{ min: 0, step: 0.01 }}
                                />
                                <TextField
                                    size="small"
                                    label="Max CF"
                                    type="number"
                                    value={tier.maxCbf}
                                    onChange={updateTier(idx, "maxCbf")}
                                    inputProps={{ min: 0 }}
                                />
                            </Box>
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
                                <TextField
                                    size="small"
                                    label="Consolidation"
                                    value={tier.consolidation}
                                    onChange={updateTier(idx, "consolidation")}
                                    placeholder="e.g. Free Consolidation"
                                />
                                <TextField
                                    size="small"
                                    label="Insurance Label"
                                    value={tier.insuranceLabel}
                                    onChange={updateTier(idx, "insuranceLabel")}
                                    placeholder="e.g. 3% (Min $15)"
                                />
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={tier.highlight}
                                            onChange={updateTier(idx, "highlight")}
                                        />
                                    }
                                    label="Highlight badge"
                                />
                                {tier.highlight && (
                                    <TextField
                                        size="small"
                                        label="Badge text"
                                        value={tier.highlightLabel}
                                        onChange={updateTier(idx, "highlightLabel")}
                                        placeholder="e.g. Limited Time Offer!"
                                        sx={{ flex: 1, minWidth: 180 }}
                                    />
                                )}
                                <IconButton size="small" color="error" onClick={() => removeTier(idx)} sx={{ ml: "auto" }}>
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    ))}
                </CardContent>
            </Card>

            <Card>
                <CardHeader
                    title={<Typography fontWeight={700} fontSize={14}>Delivery Fees</Typography>}
                    subheader="Add one or more delivery fee/route options — shown together on the public rates page"
                    action={
                        <Button size="small" startIcon={<Add />} onClick={addDeliveryFee}>
                            Add Delivery Fee
                        </Button>
                    }
                />
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {deliveryFees.map((d, idx) => (
                        <Box key={idx} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr auto" }, gap: 1.5, alignItems: "center" }}>
                            <TextField
                                size="small"
                                label="Route Label"
                                value={d.route}
                                onChange={updateDeliveryFee(idx, "route")}
                                placeholder="e.g. Savannah → George Town"
                            />
                            <TextField
                                size="small"
                                label="Delivery Fee ($)"
                                type="number"
                                value={d.fee}
                                onChange={updateDeliveryFee(idx, "fee")}
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                            <IconButton size="small" color="error" onClick={() => removeDeliveryFee(idx)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </CardContent>
            </Card>

            <Card>
                <CardHeader
                    title={<Typography fontWeight={700} fontSize={14}>Perks List</Typography>}
                    action={
                        <Button size="small" startIcon={<Add />} onClick={addPerk}>
                            Add Perk
                        </Button>
                    }
                />
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    {perks.map((perk, idx) => (
                        <Box key={idx} sx={{ display: "flex", gap: 1 }}>
                            <TextField
                                size="small"
                                fullWidth
                                value={perk}
                                onChange={updatePerk(idx)}
                                placeholder="e.g. No handling fees"
                            />
                            <IconButton size="small" color="error" onClick={() => removePerk(idx)}>
                                <Delete fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </CardContent>
            </Card>

            <Box>
                <Button variant="contained" startIcon={<Save />} onClick={handleSave} disabled={saving}>
                    {saving ? <CircularProgress size={18} color="inherit" /> : "Save Rates"}
                </Button>
            </Box>
        </Box>
    );
};

export default AdminShopperRates;